import React, {useCallback, useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import styled from 'styled-components'
import {HOST_MAX_PRICE, REGEX_NUMBER, WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {formatCurrency, getValueOfCurrency} from 'utils'
import {Button, Input, ModalLoading} from 'common/components'
import {VALIDATION_TREE_MAX_PRICE} from 'common/validations'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'
import {TreeEditProfileFormNumberProps} from './TreeEditProfileFormProps'

const StyledContainer = styled.div`
  width: 100%;
  background-color: ${({theme}) => theme.white_1};
  max-width: ${convertUnit(480)};
  padding: ${convertUnit(40)};
  border-radius: ${convertUnit(16)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    max-width: 100%;
    padding: ${convertUnit(20)};
    border-radius: ${convertUnit(0)};
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  margin-top: ${convertUnit(20)};
`

export default function TreeEditProfileFormMaxPrice({
  defaultValue,
  stateActiveContent,
}: TreeEditProfileFormNumberProps) {
  const {translate} = useTranslation()
  const setActiveContent = stateActiveContent[1]
  const [loading, setLoading] = useState(false)
  const {minimumPrice} = useSelector('hostState')

  const form = useForm<{maxPrice: number}>({
    defaultValues: {
      maxPrice: defaultValue || 0,
    },
  })
  const {watch, errors} = form
  const {maxPrice} = watch()
  const disabled = useMemo(
    () =>
      Object.keys(errors).length > 0 ||
      getValueOfCurrency(maxPrice) === defaultValue ||
      getValueOfCurrency(maxPrice) < getValueOfCurrency(minimumPrice ?? 0),
    [errors, maxPrice, defaultValue, minimumPrice],
  )

  const handlePatchHost = useCallback(() => {
    setLoading(true)
    requestData('tree_patch_hosts', {
      useDefaultMessage: true,
      actionType: 'execute',
      data: {
        maximum_price:
          maxPrice.toString().length === 0
            ? HOST_MAX_PRICE
            : getValueOfCurrency(maxPrice),
      },
      onRequestReceived: () => setLoading(false),
      onRequestSuccess: ({status}) => {
        status === 200 && setActiveContent('profile')
      },
    })
  }, [maxPrice, setActiveContent])

  const handleRenderForm = useMemo(
    () => (
      <Input
        form={form}
        name="maxPrice"
        value={!maxPrice ? '' : formatCurrency(maxPrice)}
        allowedCharacters={REGEX_NUMBER}
        formRules={VALIDATION_TREE_MAX_PRICE(
          getValueOfCurrency(minimumPrice ?? 0),
        )}
        label={translate('tree:editHostFormLabelMaxPrice')}
        placeholder={translate('tree:editHostFormPlaceholderMaxPrice')}
        leftText={translate('global:idr')}
        max={HOST_MAX_PRICE}
      />
    ),
    [form, maxPrice, minimumPrice, translate],
  )

  const handleRenderButton = useMemo(
    () => (
      <StyledButton
        disabled={disabled}
        label={translate('global:save')}
        onClick={handlePatchHost}
      />
    ),
    [disabled, handlePatchHost, translate],
  )

  return (
    <StyledContainer>
      <ModalLoading visible={loading} />
      {handleRenderForm}
      {handleRenderButton}
    </StyledContainer>
  )
}
